import { useEffect, useState } from 'react';
import { useMediaDevicesContext } from '../../../providers/media-devices-context';
import type { BrowserAudioFlow } from '../../../services/audio-flow';
import AudioRecorder from '../../../services/audio-recorder';

type UseAudioRecorderProps = {
  audioFlow?: BrowserAudioFlow;
  isRecording: boolean;
  onAudioChunk: (chunk: ArrayBuffer) => void;
  inputSampleRate?: number | null;
};

const useAudioRecorder = ({
  audioFlow,
  isRecording,
  onAudioChunk,
  inputSampleRate,
}: UseAudioRecorderProps) => {
  const [audioRecorder, setAudioRecorder] = useState<AudioRecorder | null>(
    null,
  );
  const { selectedMicrophone, isAudioEnabled } = useMediaDevicesContext();
  useEffect(() => {
    audioFlow?.setCaptureExpected(isRecording);
  }, [audioFlow, isRecording]);

  useEffect(() => {
    // Only initialize audio recorder when we have a valid sample rate
    if (inputSampleRate == null) {
      return;
    }

    const audioRecorder = new AudioRecorder(inputSampleRate, audioFlow);
    setAudioRecorder(audioRecorder);

    return () => {
      audioRecorder.destroy();
      setAudioRecorder(null);
    };
  }, [inputSampleRate, audioFlow]);

  useEffect(() => {
    if (!audioRecorder) {
      return;
    }

    if (isRecording) {
      audioRecorder.startRecording(selectedMicrophone);
    } else {
      audioRecorder.stopRecording();
    }
  }, [isRecording, audioRecorder, selectedMicrophone]);

  useEffect(() => {
    if (!audioRecorder) {
      return;
    }

    audioRecorder.onaudiochunk = onAudioChunk;

    return () => {
      audioRecorder.onaudiochunk = undefined;
    };
  }, [onAudioChunk, audioRecorder]);

  useEffect(() => {
    if (!audioRecorder) {
      return;
    }

    audioRecorder.muted = !isAudioEnabled;
  }, [isAudioEnabled, audioRecorder]);
};

export default useAudioRecorder;
